/* eslint-disable camelcase */
/* eslint-disable no-throw-literal */
import * as Sentry from '@sentry/react'
import jwt_decode from 'jwt-decode'

import { identify, track } from '@/utils/analytics'
import {
  PASSWORD_RESET_REQUESTED,
  USER_LOGGED_IN,
  USER_LOGGED_OUT,
} from '@/utils/analytics.constants'
import { getErrorMessage, isCanceledError } from '@/utils/helpers'

/** @todo Fix import cycle */
// eslint-disable-next-line import/no-cycle
import axios from './instance'

// --------------------------- User Authentication with SSO (WPC Users) ---------------------------

/**
 * Retrieve the SSO login URL
 *
 * @param {*} setError
 * @param {*} setLoading
 * @returns `data` results or null
 */
export const getSSOLoginUrl = async (setError, setLoading) => {
  setLoading(true)

  try {
    const data = await axios.get(`/auth/o/microsoft-graph/?redirect_uri=${window.location.origin}`)
    setLoading(false)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Logs in a user with the specified `params` (code, state, session_state)
 *
 * @param {object} params
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setCurrentTokens
 * @param {func} getUpdatedUser
 * @returns `data` results or null
 */
export const loginWithSSO = async (
  params,
  setError,
  setLoading,
  setCurrentTokens,
  setCurrentUser,
) => {
  setLoading(true)

  try {
    const data = await axios.post(
      `/auth/o/microsoft-graph/?code=${params.code}&state=${params.state}&session_state=${params.session_state}`,
    )

    // Get updated user
    const token = { ...data }.access
    const decoded = jwt_decode(token)
    const updatedUser = await getUser(decoded.user_id, token)
    if (updatedUser) {
      setCurrentUser(updatedUser)

      Sentry.setUser({
        name: `${updatedUser.firstName} ${updatedUser.lastName}`,
        ...updatedUser,
      })

      // Now set the tokens since the user is fully authenticated and we have the user object
      setCurrentTokens(data)

      // Identify user before tracking login so the event is attributed correctly
      identify(updatedUser)
      track(USER_LOGGED_IN, {
        method: 'sso',
        user_type: updatedUser.userType,
        role: updatedUser.ssoUserProfile?.role || updatedUser.dealerUserProfile?.role,
      })
    }

    setLoading(false)

    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)

    return null
  }
}

// ------------------------ User Authentication with Tokens (Dealer Users) ------------------------

/**
 * Creates a new account with the specified `user` information.
 * @param {object} user
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` results
 */
export const addUser = async (user, setError, setLoading, setSuccess) => {
  setLoading(true)

  try {
    const data = await axios.post('/users/?expand=dealer_user_profile.dealers/', user)
    setSuccess('New account created.')
    setLoading(false)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Requests verification code for a user with the specified `credentials`.
 * @param {object} credentials
 * @param {func} setError
 * @param {func} setLoading
 * @returns {object} with MFA properties (message, method and obfuscated value)
 */
export const loginWithCredentials = async (credentials, setError, setLoading) => {
  setLoading(true)

  try {
    const data = await axios.post('/login/', credentials)
    setLoading(false)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Re-requests verification code (with optionally-specified MFA device type) and logs in a user with the specified `credentials`.
 * @param {object} credentials
 * @param {func} setError
 * @param {func} setLoading
 * @returns {object} with MFA properties (message, method and obfuscated value)
 */
export const resendLoginCode = async (credentials, setError, setLoading) => {
  setLoading(true)

  try {
    const data = await axios.post('/login/resend/', credentials)
    setLoading(false)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Submits verification code and logs in a user with the specified `credentials`.
 * @param {object} credentials
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setCurrentUser
 * @param {func} setCurrentTokens
 * @param {func} getUpdatedUser
 */
export const loginConfirm = async (
  payload,
  setError,
  setLoading,
  setCurrentTokens,
  setCurrentUser,
) => {
  setLoading(true)

  try {
    const data = await axios.post('/login/confirm/', payload)

    // Get updated user
    const token = { ...data }.access
    const decoded = jwt_decode(token)
    const updatedUser = await getUser(decoded.user_id, token)
    if (updatedUser) {
      setCurrentUser(updatedUser)

      Sentry.setUser({
        name: `${updatedUser.firstName} ${updatedUser.lastName}`,
        ...updatedUser,
      })

      // Now set the tokens since the user is fully authenticated and we have the user object
      setCurrentTokens(data)

      // Identify user before tracking login so the event is attributed correctly
      identify(updatedUser)
      track(USER_LOGGED_IN, {
        method: 'credentials',
        user_type: updatedUser.userType,
        role: updatedUser.ssoUserProfile?.role || updatedUser.dealerUserProfile?.role,
      })
    }

    setLoading(false)

    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)

    return null
  }
}

/**
 * Requests a password reset for the specified user
 * @param {object} payload
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 */
export const forgotPassword = async (payload, setError, setLoading, setSuccess) => {
  setLoading(true)

  try {
    const { data } = await axios.post('/password_reset/', payload)

    // Track password reset request
    track(PASSWORD_RESET_REQUESTED)

    setSuccess('Check your email to reset your password.')
    setLoading(false)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Confirms the user's new password
 * @param {object} payload
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setCurrentTokens
 * @param {func} getUpdatedUser
 * @return whether it was successful
 */
export const resetPassword = async (
  payload,
  setError,
  setLoading,
  setCurrentTokens,
  setCurrentUser,
) => {
  setLoading(true)

  try {
    const data = await axios.post('/password_reset/confirm/', payload)

    // Get updated user
    const token = { ...data }.access
    const decoded = jwt_decode(token)
    const updatedUser = await getUser(decoded.user_id, token)
    if (updatedUser) {
      setCurrentUser(updatedUser)

      Sentry.setUser({
        name: `${updatedUser.firstName} ${updatedUser.lastName}`,
        ...updatedUser,
      })

      // Now set the tokens since the user is fully authenticated and we have the user object
      setCurrentTokens(data)
    }

    setLoading(false)
    return true
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return false
  }
}

/**
 * Validates the specified `token` for the reset password flow
 * @param {string} token
 * @param {func} showError
 * @return whether it was successful
 */
export const validateResetToken = async (token, showError) => {
  try {
    await axios.post('/password_reset/validate_token/', { token })
    return true
  } catch (err) {
    showError(getErrorMessage(err))
    return false
  }
}

/**
 * Refreshes the specified `refresh` token
 * @param {string} refresh
 */
export const refreshAccessToken = async (refresh) => {
  try {
    return await axios.post('/token/refresh/', { refresh }, { skipAuthRefresh: true })
  } catch (err) {
    // Prefer the server response; fall back to err.message/err.code for network
    // errors and timeouts where `err.response` is undefined, so callers always
    // get actionable details instead of `{ error: undefined }`.
    let details
    if (typeof err.response?.data === 'object') {
      details = err.response.data
    } else if (err.response?.data !== undefined) {
      details = { error: err.response.data }
    } else {
      details = { error: err.message || err.code || String(err) }
    }
    const refreshError = new Error(
      err.message || details.error || 'Failed to refresh access token',
    )
    refreshError.error = true
    refreshError.details = details
    throw refreshError
  }
}

/**
 *
 * If possible, revoke the user's session.
 * In all cases, remove the user's data, including auth tokens, by clearing the store. This triggers
 * a change in PrivateRoute and redirects the user.
 *
 * @param {string} refresh - The user's refresh token
 * @param {function} clearStore - Function from RootStore to clear out all store data
 */
export const logout = async (refresh, clearStore = () => {}) => {
  // Track logout before clearing store (which resets analytics)
  track(USER_LOGGED_OUT)

  try {
    await axios.post('/logout/', { refresh })
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('User session could not be revoked.')
  }

  clearStore()
}

// --------------------------------- User Information ---------------------------------

/**
 * Get the specified `user`'s information
 * @param {string} id
 * @returns `data` results
 */
export const getUser = async (id, token) => {
  try {
    const data = await axios.get(
      `/users/${id}/?expand=dealer_user_profile.dealers.group,dealer_user_profile.dealer_groups,sso_user_profile`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    )
    return data
  } catch (err) {
    return null
  }
}

/**
 * Get the specified `user`'s dealer access.
 * @returns `data` results
 */
export const getDealerAccess = async (userId, setLoading, setError) => {
  setLoading(true)
  try {
    const data = await axios.get(`/users/${userId}/dealer_access`)
    setLoading(false)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Updates the specified `user`'s information
 * @param {object} user
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @param {func} setCurrentUser
 * @returns `data` results
 */
export const updateUser = async (
  user,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
  setCurrentUser = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.patch(`/users/${user.id}/`, user)

    setCurrentUser(data)
    setLoading(false)
    setSuccess('User information updated.')
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Update the specified the password of the authenticated user
 * @param {object} data
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 */
export const updateUserPassword = async (data, setError, setLoading, setSuccess) => {
  setLoading(true)

  try {
    const response = await axios.put('/update-password/', data)
    setLoading(false)
    setSuccess(response.message)
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
  }
}

/**
 * Get list of users
 * @param {string} url
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @param {AbortSignal} signal - cancels the request when a newer one supersedes it
 * @returns `data` results
 */
export const getUsers = async (
  url,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
  signal = undefined,
) => {
  setLoading(true)

  try {
    const data = await axios.get(url, { signal })

    setLoading(false)
    setSuccess(data)

    return data
  } catch (err) {
    // Superseded by a newer request; leave loading state for the in-flight one.
    if (isCanceledError(err)) return null

    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

// --------------------------------- User Deletion ---------------------------------------

/**
 * Deletes the specified `user`
 * @param {string} userId
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` results
 */
export const deleteUser = async (userId, setError, setLoading, setSuccess) => {
  setLoading(true)

  try {
    const data = await axios.delete(`/users/${userId}/`)

    setLoading(false)
    setSuccess('User deleted.')
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

// --------------------------------- Change MFA Settings ---------------------------------

/**
 * Requests an email change with the specified `email` for the authenticated user
 * @param {string} email
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` results
 */
export const requestEmailChange = async (
  email,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.post(`/change_email/`, { email })

    setLoading(false)
    setSuccess(data.message)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Requests an phone change with the specified `phone` for the authenticated user
 * @param {string} phoneNumber
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` results
 */
export const requestPhoneChange = async (
  phoneNumber,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.post(`/change_phone_number/`, { phoneNumber })

    setLoading(false)
    setSuccess(data.message ? data.message : 'Phone number removed.')
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Submits the specified verification `code`.
 * @param {string} code
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @param {func} setCurrentUser
 * @returns `data` results
 */
export const confirmEmailChange = async (
  code,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
  setCurrentUser = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.post(`/change_email/confirm/`, { code })

    setCurrentUser(data)
    setLoading(false)
    setSuccess('Email address updated.')
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Submits the specified verification `code`.
 * @param {string} code
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @param {func} setCurrentUser
 * @returns `data` results
 */
export const confirmPhoneChange = async (
  code,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
  setCurrentUser = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.post(`/change_phone_number/confirm/`, { code })

    setCurrentUser(data)
    setLoading(false)
    setSuccess('Phone number updated.')
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

// --------------------------------- Unused ---------------------------------

export function confirmAccountOrEmail(token, uid) {
  return axios.get(`/confirm/?token=${token}&uid=${uid}`)
}

// --------------------------------- Exports ---------------------------------

/**
 * Create a new user export.
 *
 * @param {string} url Endpoint to call to initiate the export.
 * @param {func} [setError] Callback to set an error message.
 * @param {func} [setLoading] Callback to toggle loading state.
 * @returns {*} Axios response data or `null` on error.
 */
export const createUserExport = async (url, setError = () => {}, setLoading = () => {}) => {
  setLoading(true)

  try {
    const response = await axios.post(url)

    setLoading(false)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Retrieve a user export by its identifier.
 *
 * @param {string|number} exportId Identifier of the export to retrieve.
 * @param {func} [setError] Callback to set an error message.
 * @param {func} [setLoading] Callback to toggle loading state.
 * @returns {*} Axios response data or `null` on error.
 */
export const getUserExport = async (exportId, setError = () => {}, setLoading = () => {}) => {
  setLoading(true)

  try {
    const response = await axios.get(`/user-exports/${exportId}`)

    setLoading(false)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}
